import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import HelcimPayButton from "@/components/HelcimPayButton";
import PaymentBreakdown from "@/components/PaymentBreakdown";
import { money, StatusBadge, priceLabel } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  ArrowLeft, Send, ShieldCheck, HandCoins, KeyRound, CheckCircle2, Clock,
  ShoppingCart, User, Copy, XCircle, Loader2, UserCheck, Wallet as WalletIcon, Package,
} from "lucide-react";

export default function MatchRoom() {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const [match, setMatch] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [fee, setFee] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  const loadMatch = useCallback(async () => {
    try {
      const { data } = await api.get(`/matches/${matchId}`);
      setMatch(data);
    } catch (err) {
      toast.error("Cannot access this room");
      navigate("/my-deals");
    }
  }, [matchId, navigate]);

  const loadMessages = useCallback(async () => {
    try {
      const { data } = await api.get(`/matches/${matchId}/messages`);
      setMessages(data);
    } catch (err) { console.debug("messages poll skipped:", err?.message); }
  }, [matchId]);

  useEffect(() => {
    loadMatch();
    loadMessages();
    const iv = setInterval(() => {
      loadMessages();
      loadMatch();
    }, 2500);
    return () => clearInterval(iv);
  }, [loadMatch, loadMessages]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    const t = text;
    setText("");
    try {
      await api.post(`/matches/${matchId}/messages`, { text: t });
      loadMessages();
    } catch {
      toast.error("Failed to send");
    }
  };

  const act = async (fn, successMsg) => {
    setBusy(true);
    try {
      await fn();
      if (successMsg) toast.success(successMsg);
      await loadMatch();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Action failed");
    } finally {
      setBusy(false);
    }
  };

  const payFromWallet = async () => {
    setBusy(true);
    try {
      await api.post(`/matches/${matchId}/pay/wallet`);
      toast.success("Fee paid from your wallet");
      await refresh();
      await loadMatch();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Wallet payment failed");
    } finally {
      setBusy(false);
    }
  };

  if (!match) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="mx-auto max-w-6xl px-4 py-10 text-muted-foreground">Loading room…</div>
      </div>
    );
  }

  const isBuyer = user.user_id === match.primary_user_id;
  const isPartner = user.user_id === match.partner_user_id;
  const otherName = isBuyer ? (match.partner_name || "Partner (pending)") : match.primary_name;
  const hasPartner = !!match.partner_user_id;
  const adminFee = match.pricing?.admin_fee || 0;
  const walletTotal = Math.round(((match.agreed_fee || 0) + adminFee) * 100) / 100;
  const cardTotal = match.pricing?.total_partner_pays ?? walletTotal;

  return (
    <div className="min-h-screen bg-background grain-bg">
      <Navbar />
      <main className="mx-auto max-w-6xl px-4 sm:px-6 py-6">
        <button
          onClick={() => navigate("/my-deals")}
          data-testid="back-to-my-deals"
          className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-4 w-4" /> My deals
        </button>

        <div className="mt-4 flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl tracking-tight" style={{ fontWeight: 700 }}>
              {match.deal?.title}
            </h1>
            <p className="text-sm text-muted-foreground">
              {isBuyer ? "You are the buyer" : "You are the partner"} · with {otherName}
            </p>
          </div>
          <StatusBadge status={match.status} testid="room-status-badge" />
        </div>

        {match.hold_expiring && (
          <div data-testid="hold-expiring-banner" className="mt-4 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 flex items-center gap-2">
            <Clock className="h-4 w-4 shrink-0" />
            Card hold expires in ~{match.hold_hours_left}h. Finish the code exchange now — once it lapses, the fee can no longer be collected.
          </div>
        )}
        {match.status === "expired" && (
          <div data-testid="hold-expired-banner" className="mt-4 rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700 flex items-center gap-2">
            <XCircle className="h-4 w-4 shrink-0" />
            The card hold expired before completion, so no funds were captured. The partner can place a new hold to continue.
          </div>
        )}

        <div className="mt-6 grid lg:grid-cols-5 gap-6">
          {/* Chat */}
          <div className="lg:col-span-3 flex flex-col rounded-xl border border-black/10 bg-white overflow-hidden h-[560px]">
            <div className="border-b border-black/10 px-5 py-3 flex items-center gap-2 bg-secondary/50">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-foreground text-background text-xs font-semibold">
                {otherName?.charAt(0)?.toUpperCase() || <User className="h-4 w-4" />}
              </div>
              <div>
                <p className="text-sm font-semibold leading-none">{otherName}</p>
                <p className="text-xs text-muted-foreground">Live chat</p>
              </div>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-3 no-scrollbar" data-testid="chat-messages">
              {!hasPartner ? (
                <div className="h-full flex flex-col items-center justify-center text-center text-muted-foreground">
                  <Clock className="h-8 w-8 mb-3" />
                  <p className="text-sm">Waiting for a partner to join this deal.<br />Chat opens once they do.</p>
                </div>
              ) : messages.length === 0 ? (
                <div className="h-full flex items-center justify-center text-sm text-muted-foreground">
                  Say hello and agree on the details.
                </div>
              ) : (
                messages.map((m) => {
                  const mine = m.sender_id === user.user_id;
                  return (
                    <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div
                        data-testid="chat-bubble"
                        className={`max-w-[75%] rounded-2xl px-4 py-2 text-sm ${
                          mine
                            ? "bg-primary text-primary-foreground rounded-br-sm"
                            : "bg-secondary text-foreground rounded-bl-sm"
                        }`}
                      >
                        {!mine && <p className="text-[11px] font-semibold opacity-70 mb-0.5">{m.sender_name}</p>}
                        {m.text}
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            <form onSubmit={sendMessage} className="border-t border-black/10 p-3 flex items-center gap-2 bg-white/70 backdrop-blur-xl">
              <Input
                value={text}
                data-testid="chat-input"
                disabled={!hasPartner}
                onChange={(e) => setText(e.target.value)}
                placeholder={hasPartner ? "Type a message…" : "Chat locked until partner joins"}
                className="rounded-full"
              />
              <Button type="submit" size="icon" data-testid="chat-send-btn" disabled={!hasPartner} className="rounded-full shrink-0">
                <Send className="h-4 w-4" />
              </Button>
            </form>
          </div>

          {/* Workflow */}
          <div className="lg:col-span-2 space-y-5">
            <div className="rounded-xl border border-black/10 bg-white p-5">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Deal value</span>
                <span className="font-heading text-lg" style={{ fontWeight: 700 }}>{priceLabel(match.deal)}</span>
              </div>
              {match.agreed_fee != null && (
                <div className="mt-2 flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Agreed fee (buyer receives)</span>
                  <span className="font-heading text-lg text-accent" style={{ fontWeight: 700 }}>{money(match.agreed_fee)}</span>
                </div>
              )}
              {match.agreed_fee != null && match.pricing && (
                <div className="mt-3">
                  <PaymentBreakdown pricing={match.pricing} mode="agreed" />
                </div>
              )}
            </div>

            {/* Bulk Buying Co-op basket */}
            {match.deal?.line_items?.length > 0 && (
              <div data-testid="room-coop-basket" className="rounded-xl border border-blue-200 bg-white p-5">
                <div className="flex items-center gap-2 font-semibold text-sm">
                  <Package className="h-4 w-4 text-primary" /> Co-op basket
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  The partner takes the highlighted items and pays only their share.
                </p>
                <div className="mt-3 space-y-2">
                  {match.deal.line_items.map((it) => {
                    const picked = (match.selected_items || []).includes(it.item_id);
                    return (
                      <div key={it.item_id} data-testid={`room-coop-item-${it.item_id}`}
                        className={`flex items-center justify-between rounded-md border p-2.5 text-sm ${picked ? "border-blue-400 bg-blue-50" : "border-black/10 opacity-60"}`}>
                        <div className="flex items-center gap-2.5">
                          {it.image_url && (
                            <img src={it.image_url} alt="" className="h-9 w-9 rounded object-cover border border-black/10" />
                          )}
                          <div>
                            <p className="font-medium">
                              {it.name}
                              {picked && <span className="ml-1.5 text-[11px] font-semibold text-blue-700">· selected</span>}
                            </p>
                            <p className="text-[11px] text-muted-foreground">Unit {money(it.unit_price)}</p>
                          </div>
                        </div>
                        <span className="font-semibold text-accent">{money(it.share)}</span>
                      </div>
                    );
                  })}
                </div>
                {match.coop_selected_total != null && (
                  <div className="mt-3 flex items-center justify-between rounded-md bg-secondary p-2.5 text-sm">
                    <span className="text-muted-foreground">Partner's selected share</span>
                    <span data-testid="room-coop-selected-total" className="font-semibold text-accent">{money(match.coop_selected_total)}</span>
                  </div>
                )}
              </div>
            )}

            {/* In-person requirement */}            {match.deal?.both_present_required && (
              <div className="rounded-xl border border-black/10 bg-white p-5">
                <div className="flex items-center gap-2 font-semibold text-sm">
                  <UserCheck className="h-4 w-4 text-primary" /> In-person requirement
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  Both parties must be present together in-store when the item(s) are purchased.
                </p>
                {match.both_present_waived ? (
                  <span data-testid="presence-waived-badge" className="mt-2 inline-flex items-center gap-1 rounded-full bg-secondary px-2.5 py-0.5 text-xs font-semibold text-muted-foreground">
                    <CheckCircle2 className="h-3 w-3" /> Waived by partner
                  </span>
                ) : isPartner ? (
                  <Button variant="outline" size="sm" data-testid="waive-presence-btn"
                    className="mt-2 rounded-full border-black/20"
                    onClick={() => act(() => api.post(`/matches/${matchId}/waive-presence`), "In-person requirement waived")}>
                    Waive in-person requirement
                  </Button>
                ) : (
                  <p className="mt-2 text-xs text-muted-foreground">Only the partner can waive this.</p>
                )}
              </div>
            )}

            {/* Step: negotiate fee */}
            {hasPartner && ["matched", "negotiating"].includes(match.status) && (
              <div className="rounded-xl border border-black/10 bg-white p-5">
                <div className="flex items-center gap-2 font-semibold text-sm">
                  <HandCoins className="h-4 w-4 text-primary" /> Agree the partner's fee
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  The amount the partner pays the buyer for the second item.
                </p>

                {match.pricing && (
                  <div className="mt-3">
                    <PaymentBreakdown pricing={match.pricing} mode="recommended" />
                    <Button
                      variant="outline" disabled={busy}
                      data-testid="use-recommended-fee-btn"
                      onClick={() => act(async () => {
                        await api.post(`/matches/${matchId}/fee`, { amount: match.pricing.recommended_fee });
                      }, "Recommended fee proposed")}
                      className="mt-2 w-full rounded-full border-black/20"
                    >
                      Use recommended fee · {money(match.pricing.recommended_fee)}
                    </Button>
                  </div>
                )}

                {match.proposed_fee != null && (
                  <div className="mt-3 rounded-lg bg-secondary p-3 text-sm">
                    Proposed: <b>{money(match.proposed_fee)}</b>{" "}
                    {match.fee_proposed_by === user.user_id ? "(by you — waiting for the other party)" : "(waiting for your response)"}
                  </div>
                )}

                {match.proposed_fee != null && match.fee_proposed_by !== user.user_id && (
                  <Button
                    data-testid="accept-fee-btn" disabled={busy}
                    className="mt-3 w-full rounded-full"
                    onClick={() => act(() => api.post(`/matches/${matchId}/fee/accept`), "Fee agreed!")}
                  >
                    Accept {money(match.proposed_fee)}
                  </Button>
                )}

                <div className="mt-3 flex gap-2">
                  <Input
                    type="number" min="0" step="0.5" value={fee}
                    data-testid="fee-input"
                    onChange={(e) => setFee(e.target.value)}
                    placeholder="Propose amount"
                  />
                  <Button
                    variant="outline" disabled={busy || !fee}
                    data-testid="propose-fee-btn"
                    onClick={() => act(async () => {
                      await api.post(`/matches/${matchId}/fee`, { amount: parseFloat(fee) });
                      setFee("");
                    }, "Fee proposed")}
                    className="rounded-full shrink-0"
                  >
                    Propose
                  </Button>
                </div>
              </div>
            )}

            {/* Step: partner pays */}
            {match.status === "fee_agreed" && (
              <div className="rounded-xl border border-black/10 bg-white p-5">
                <div className="flex items-center gap-2 font-semibold text-sm">
                  <HandCoins className="h-4 w-4 text-primary" /> Payment
                </div>
                {isPartner ? (
                  <>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Place a hold of {money(cardTotal)} on your card (Helcim). No money moves until you confirm the code — then it's captured. Holds expire in 7 days.
                    </p>
                    <HelcimPayButton matchId={matchId} amount={cardTotal} onPaid={loadMatch} />
                    <div className="mt-4 border-t border-black/5 pt-4">
                      <p className="text-xs text-muted-foreground">
                        Or pay instantly from your wallet · balance {money(user.wallet_balance || 0)}
                      </p>
                      <Button
                        data-testid="pay-from-wallet-btn"
                        onClick={payFromWallet}
                        disabled={busy || (user.wallet_balance || 0) < walletTotal}
                        variant="outline"
                        className="mt-2 w-full rounded-full border-black/20"
                      >
                        <WalletIcon className="mr-1.5 h-4 w-4" />
                        {(user.wallet_balance || 0) < walletTotal
                          ? `Insufficient wallet balance`
                          : `Pay ${money(walletTotal)} from wallet`}
                      </Button>
                      {(user.wallet_balance || 0) < walletTotal && (
                        <button
                          data-testid="topup-link"
                          onClick={() => navigate("/wallet")}
                          className="mt-1.5 text-xs text-primary hover:underline"
                        >
                          Add money to your wallet →
                        </button>
                      )}
                    </div>
                  </>
                ) : (
                  <p className="mt-1 text-sm text-muted-foreground flex items-center gap-2">
                    <Clock className="h-4 w-4" /> Waiting for the partner to pay {money(cardTotal)}.
                  </p>
                )}
              </div>
            )}

            {/* Step: buyer submits code */}
            {match.status === "paid" && (
              <div className="rounded-xl border border-black/10 bg-white p-5">
                <div className="flex items-center gap-2 font-semibold text-sm">
                  <KeyRound className="h-4 w-4 text-primary" /> Code exchange
                </div>
                {isBuyer ? (
                  <>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Partner's card hold is in place. Share the redemption code so they can claim the second item.
                    </p>
                    <div className="mt-3 flex gap-2">
                      <Input
                        value={code} data-testid="code-input"
                        onChange={(e) => setCode(e.target.value)}
                        placeholder="e.g. BOGO-4X9K2"
                        className="font-mono-code"
                      />
                      <Button
                        disabled={busy || !code}
                        data-testid="submit-code-btn"
                        onClick={() => act(() => api.post(`/matches/${matchId}/code`, { code }), "Code shared")}
                        className="rounded-full shrink-0"
                      >
                        Share
                      </Button>
                    </div>
                  </>
                ) : (
                  <p className="mt-1 text-sm text-muted-foreground flex items-center gap-2">
                    <Clock className="h-4 w-4" /> Your card hold is placed. Waiting for the buyer to share the code.
                  </p>
                )}
              </div>
            )}

            {/* Step: partner confirms code */}
            {match.status === "code_shared" && (
              <div className="rounded-xl border-2 border-primary/30 bg-white p-5">
                <div className="flex items-center gap-2 font-semibold text-sm">
                  <KeyRound className="h-4 w-4 text-primary" /> Redemption code
                </div>
                <div className="mt-3 rounded-lg bg-foreground text-background p-4 text-center">
                  <p className="text-[11px] uppercase tracking-widest text-background/60">Secure code</p>
                  <p className="mt-1 font-mono-code text-2xl tracking-widest" data-testid="redemption-code">{match.redemption_code}</p>
                </div>
                {isPartner ? (
                  <>
                    <button
                      onClick={() => { navigator.clipboard?.writeText(match.redemption_code); toast.success("Copied"); }}
                      className="mt-2 mx-auto flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                    >
                      <Copy className="h-3 w-3" /> Copy code
                    </button>
                    <p className="mt-3 text-xs text-muted-foreground">Confirm once the code works and you've claimed your item. This captures your card hold and releases the fee to the buyer.</p>
                    <Button
                      data-testid="confirm-code-btn" disabled={busy}
                      className="mt-3 w-full rounded-full hard-shadow-sm hover:-translate-y-0.5 transition-transform duration-200"
                      onClick={() => act(() => api.post(`/matches/${matchId}/code/confirm`), "Confirmed — payment captured!")}
                    >
                      <CheckCircle2 className="mr-2 h-4 w-4" /> Confirm code &amp; capture payment
                    </Button>
                  </>
                ) : (
                  <p className="mt-3 text-sm text-muted-foreground flex items-center gap-2">
                    <Clock className="h-4 w-4" /> Waiting for the partner to confirm the code.
                  </p>
                )}
              </div>
            )}

            {/* Settled */}
            {match.status === "code_confirmed" && (
              <div className="rounded-xl border border-emerald-300 bg-emerald-50 p-5">
                <div className="flex items-center gap-2 font-semibold text-sm text-emerald-800">
                  <ShieldCheck className="h-4 w-4" /> Verified — payout pending
                </div>
                <p className="mt-1 text-sm text-emerald-700">
                  Code confirmed. The admin will transfer {money(match.agreed_fee)} to the buyer via e-transfer.
                </p>
              </div>
            )}
            {match.status === "completed" && (
              <div className="rounded-xl border border-green-400 bg-green-50 p-5">
                <div className="flex items-center gap-2 font-semibold text-sm text-green-800">
                  <CheckCircle2 className="h-4 w-4" /> Deal completed
                </div>
                <p className="mt-1 text-sm text-green-700">
                  Funds of {money(match.agreed_fee)} were transferred to the buyer. 🎉
                </p>
              </div>
            )}

            {/* cancel */}
            {!["completed", "code_confirmed", "cancelled"].includes(match.status) && hasPartner && (
              <Button
                variant="ghost" data-testid="cancel-match-btn"
                className="w-full text-destructive hover:text-destructive hover:bg-destructive/10"
                onClick={() => act(() => api.post(`/matches/${matchId}/cancel`), "Deal cancelled")}
              >
                <XCircle className="mr-2 h-4 w-4" /> Cancel this split
              </Button>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
